import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ThemeToggle from '../design-system/primitives/ThemeToggle';
import Btn from '../design-system/primitives/Btn';
import Icon from '../design-system/primitives/Icon';
import Segmented from '../design-system/primitives/Segmented';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import EntitySearch from '../components/application-analytics/EntitySearch';
import OverviewPanel from '../components/application-analytics/OverviewPanel';
import EntityPanel from '../components/application-analytics/EntityPanel';
import LiveIndicator from '../components/application-analytics/LiveIndicator';
import useLiveRefresh from '../components/application-analytics/useLiveRefresh';
import './AccountPage.css';
import './ApplicationAnalyticsPage.css';

const WINDOWS = [{ value: '7d', label: '7 days' }, { value: '30d', label: '30 days' }, { value: '90d', label: '90 days' }];
const RECENT_KEY = 'qa.recent';
const RECENT_MAX = 6;
const REFRESH_SEC = 10;

const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };

const Wordmark = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--fg-strong)', lineHeight: 1, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 18, letterSpacing: '-0.025em' }}>
    Qombra
  </span>
);

/**
 * Staff-only customer usage dashboard. Selection lives in the URL
 * (?org=3 | ?user=12, &window=30d) so a view can be pasted to a colleague.
 * The API enforces is_staff; the page just renders the refusal nicely.
 */
const ApplicationAnalyticsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org');
  const userId = params.get('user');
  const windowLabel = WINDOWS.some((w) => w.value === params.get('window')) ? params.get('window') : '30d';

  const [account, setAccount] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recent, setRecent] = useState(readRecent);
  const [live, setLive] = useState({ outcome: null, updating: true, at: null, refreshes: 0 });
  const { tick, active, online } = useLiveRefresh(REFRESH_SEC * 1000);
  // Only the newest request may write state; a refresh never overtakes a navigation.
  const requestId = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    apiService.getAccount().then((res) => setAccount(res.data)).catch(() => {});
  }, []);

  const select = useCallback((item) => {
    const next = new URLSearchParams();
    if (item) next.set(item.type === 'org' ? 'org' : 'user', String(item.id));
    if (windowLabel !== '30d') next.set('window', windowLabel);
    setParams(next);
  }, [setParams, windowLabel]);

  const setWindow = (value) => {
    const next = new URLSearchParams(params);
    if (value === '30d') next.delete('window'); else next.set('window', value);
    setParams(next);
  };

  // `background` refreshes the current view in place: no dimming, no toast,
  // no reordering of the recent list; failures only show on the live dot.
  const load = useCallback((background) => {
    const id = ++requestId.current;
    const current = () => id === requestId.current;
    inFlight.current = true;
    setLive((l) => ({ ...l, updating: true, refreshes: l.refreshes + 1 }));
    if (!background) {
      setLoading(true);
      setError(null);
    }
    const req = orgId
      ? apiService.qaOrganization(orgId, { window: windowLabel })
      : userId
        ? apiService.qaUser(userId, { window: windowLabel })
        : apiService.qaOverview({ window: windowLabel });
    req.then((res) => {
      if (!current()) return;
      setData(res.data);
      setLive((l) => ({ ...l, updating: false, outcome: 'ok', at: new Date() }));
      if (!background && res.data.entity) {
        const entry = { type: orgId ? 'org' : 'user', id: res.data.entity.id, name: res.data.entity.name };
        setRecent((prev) => {
          const list = [entry, ...prev.filter((r) => !(r.type === entry.type && r.id === entry.id))].slice(0, RECENT_MAX);
          localStorage.setItem(RECENT_KEY, JSON.stringify(list));
          return list;
        });
      }
    }).catch((err) => {
      if (!current()) return;
      setLive((l) => ({ ...l, updating: false, outcome: err.response ? 'error' : 'offline' }));
      if (background) return;
      if (err.response?.status === 403) { setForbidden(true); return; }
      const msg = err.response?.status === 404 ? 'That organization or user does not exist.' : 'Could not load analytics.';
      setError(msg);
      toast(msg);
    }).finally(() => {
      if (!current()) return;
      inFlight.current = false;
      setLoading(false);
    });
  }, [orgId, userId, windowLabel, toast]);

  useEffect(() => { load(false); }, [load]);
  // Skip a tick while a request is still out rather than cancel it: a slow
  // view would otherwise never finish loading.
  useEffect(() => {
    if (tick && !inFlight.current) load(true);
  }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedKey = orgId ? `org-${orgId}` : userId ? `user-${userId}` : null;

  return (
    <div className="account-root qa-root">
      <nav className="account-nav">
        <div className="account-nav__inner">
          <Link to="/" style={{ textDecoration: 'none' }}><Wordmark /></Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LiveIndicator live={live} active={active} online={online} intervalSec={REFRESH_SEC} />
            <ThemeToggle size={34} />
            <Btn size="sm" kind="ghost" icon="arrow-left" onClick={() => navigate('/app')}>Back to console</Btn>
          </div>
        </div>
      </nav>

      {forbidden || (account && !account.user?.is_staff) ? (
        <div className="account-shell qa-shell qa-shell--single">
          <section className="account-card qa-forbidden">
            <Icon name="lock" size={22} />
            <h1 className="account-card__title">Staff only</h1>
            <p className="qa-muted">Application analytics is available to Qombra staff accounts.</p>
          </section>
        </div>
      ) : (
        <div className="account-shell qa-shell">
          <aside className="account-sidebar qa-sidebar">
            <div className="qa-sidebar__section">
              <EntitySearch onSelect={select} />
            </div>
            <button type="button" className={`account-sidebar__item ${!selectedKey ? 'account-sidebar__item--active' : ''}`} onClick={() => select(null)}>
              <Icon name="layout-dashboard" size={14} /> All customers
            </button>
            <div className="qa-sidebar__section">
              <div className="qa-sidebar__label">Window</div>
              <Segmented options={WINDOWS} value={windowLabel} onChange={setWindow} />
            </div>
            {recent.length > 0 && (
              <div className="qa-sidebar__section">
                <div className="qa-sidebar__label">Recent</div>
                {recent.map((r) => (
                  <button
                    key={`${r.type}-${r.id}`}
                    type="button"
                    className={`account-sidebar__item ${selectedKey === `${r.type}-${r.id}` ? 'account-sidebar__item--active' : ''}`}
                    onClick={() => select(r)}
                  >
                    <Icon name={r.type === 'org' ? 'building-2' : 'user'} size={14} />
                    <span className="qa-ellipsis">{r.name}</span>
                  </button>
                ))}
              </div>
            )}
          </aside>

          <main className="account-main qa-main">
            {error && <p className="account-error">{error}</p>}
            {loading && !data && (
              <div className="qa-loading">
                <div className="qa-skeleton qa-skeleton--title" />
                <div className="qa-tiles">{[0, 1, 2, 3].map((i) => <div key={i} className="qa-skeleton qa-skeleton--tile" />)}</div>
                <div className="qa-skeleton qa-skeleton--block" />
              </div>
            )}
            {data && (
              <div className={loading ? 'qa-dim' : ''}>
                {data.entity
                  ? <EntityPanel key={selectedKey} data={data} windowLabel={windowLabel} onSelect={select} refreshKey={tick} />
                  : <OverviewPanel data={data} windowLabel={windowLabel} onSelect={select} />}
              </div>
            )}
          </main>
        </div>
      )}
    </div>
  );
};

export default ApplicationAnalyticsPage;
